import { z } from "zod";
import { CHAIN_STRATEGY_VALUES } from "../../constants/chain-strategy.js";
import { CONVERGENCE_STRATEGY_VALUES } from "../../constants/convergence-strategy.js";
import { COUNTDOWN_SECONDS_VALUES, DECISION_CHOICES_MAX, DECISION_CHOICES_MIN } from "../../constants/decision.js";
import { ENDING_RARITY_VALUES } from "../../constants/ending-rarity.js";
import { BRIEF_MAX_LENGTH, BRIEF_MIN_LENGTH, CHOICE_LABEL_MAX_LENGTH, CHOICE_SUBTITLE_MAX_LENGTH, DECISION_QUESTION_MAX_LENGTH, ENDING_NAME_MAX_LENGTH, NODE_TITLE_MAX_LENGTH, SHOT_ACTION_MAX_LENGTH, SHOT_PURPOSE_MAX_LENGTH, STORY_SYNOPSIS_MAX_LENGTH, STORY_TITLE_MAX_LENGTH, } from "../../constants/limits.js";
import { RENDER_MODE_VALUES } from "../../constants/render-mode.js";
import { FlagConditionSchema, FlagEffectSchema, StoryFlagDefSchema } from "../narrative/flags.js";
const ALLOWED_COUNTDOWN_SECONDS = new Set(COUNTDOWN_SECONDS_VALUES);
export const copilotEpisodeRequestSchema = z.object({
    brief: z.string().min(BRIEF_MIN_LENGTH).max(BRIEF_MAX_LENGTH),
});
export const refineShotRequestSchema = z.object({
    storyId: z.uuid(),
    shotId: z.uuid().optional(),
    action: z.string().min(BRIEF_MIN_LENGTH).max(SHOT_ACTION_MAX_LENGTH),
});
export const refineShotResponseSchema = z.object({
    action: z.string().min(1).max(SHOT_ACTION_MAX_LENGTH),
});
export const planChoiceSchema = z.object({
    label: z.string().min(1).max(CHOICE_LABEL_MAX_LENGTH),
    subtitle: z.string().max(CHOICE_SUBTITLE_MAX_LENGTH).nullable(),
    nextNodeIndex: z.number().int().nonnegative(),
    effects: z.array(FlagEffectSchema),
});
export const planDecisionSchema = z
    .object({
    question: z.string().min(1).max(DECISION_QUESTION_MAX_LENGTH),
    timeoutSeconds: z
        .number()
        .int()
        .refine((value) => ALLOWED_COUNTDOWN_SECONDS.has(value), "Durée de countdown non autorisée"),
    defaultChoiceIndex: z.number().int().nonnegative(),
    choices: z.array(planChoiceSchema).min(DECISION_CHOICES_MIN).max(DECISION_CHOICES_MAX),
})
    .refine((decision) => decision.defaultChoiceIndex < decision.choices.length, "defaultChoiceIndex doit référencer un choix de la décision");
export const planShotSchema = z.object({
    purpose: z.string().min(1).max(SHOT_PURPOSE_MAX_LENGTH),
    action: z.string().min(1).max(SHOT_ACTION_MAX_LENGTH),
    endAction: z.string().min(1).max(SHOT_ACTION_MAX_LENGTH).nullable(),
    durationSeconds: z.number().positive(),
    renderMode: z.enum(RENDER_MODE_VALUES),
    chainStrategy: z.enum(CHAIN_STRATEGY_VALUES),
    locationIndex: z.number().int().nonnegative().nullable(),
    characterIndexes: z.array(z.number().int().nonnegative()),
});
export const planNodeSchema = z
    .object({
    title: z.string().min(1).max(NODE_TITLE_MAX_LENGTH),
    convergenceStrategy: z.enum(CONVERGENCE_STRATEGY_VALUES).nullable(),
    shots: z.array(planShotSchema).min(1),
    decision: planDecisionSchema.nullable(),
    nextNodeIndex: z.number().int().nonnegative().nullable(),
    endingIndex: z.number().int().nonnegative().nullable(),
})
    .refine((node) => [node.decision, node.nextNodeIndex, node.endingIndex].filter((outcome) => outcome !== null).length <= 1, "Issue exclusive : décision, transition directe ou fin — un nœud sans issue est terminal, résolu par les règles de fins");
export const planEndingSchema = z.object({
    name: z.string().min(1).max(ENDING_NAME_MAX_LENGTH),
    rarity: z.enum(ENDING_RARITY_VALUES),
});
export const planEndingRuleSchema = z.object({
    endingIndex: z.number().int().nonnegative(),
    priority: z.number().int().nonnegative(),
    conditions: z.array(FlagConditionSchema),
});
export const episodePlanSchema = z.object({
    title: z.string().min(1).max(STORY_TITLE_MAX_LENGTH),
    synopsis: z.string().min(1).max(STORY_SYNOPSIS_MAX_LENGTH),
    flags: z.array(StoryFlagDefSchema),
    nodes: z.array(planNodeSchema).min(3).max(30),
    endings: z.array(planEndingSchema).min(2).max(6),
    endingRules: z.array(planEndingRuleSchema),
    entryNodeIndex: z.number().int().nonnegative(),
});
export const commitEpisodePlanSchema = z.object({
    plan: episodePlanSchema,
});
